import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { Column } from "@faire/web/slate/Layout";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import * as React from "react";
import { useRef } from "react";
import styled, { css } from "styled-components";

import {
  createFallbackHandler,
  FallbackOpacityAnimation,
} from "@faire/retailer/components/GhostLoading/FallbackHandler";
import { DiscoveryProductTile } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile";
import { useProductTileClickImpressions } from "@faire/retailer/hooks/ProductTileImpression/useProductTileClickImpressions";
import { useProductTileViewImpression } from "@faire/retailer/hooks/ProductTileImpression/useProductTileViewImpression";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import {
  SignUpElement,
  SignUpType,
  useTrackSignupModalOpenClick,
} from "@faire/retailer/lib/popSignUp";
import { getSettingEnableLoggedOutProductTileRedesign } from "@faire/retailer/settings/getSettingEnableLoggedOutProductTileRedesign";
import { preloadNavigationToPDP } from "@faire/retailer/views/product/preload/preloadNavigationToPDP";
import { PRODUCT_GRID_IMAGE_BREAKPOINTS } from "@faire/retailer/views/search/const";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productClickForSearchPageParametersILeanProductTileAdapter } from "../ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import { IProductImageProps } from "../ProductImage";

import { Container } from "./Container";
import { LoggedOutDetailSection } from "./LoggedOutDetailSection/LoggedOutDetailSection";
import { ProductImageSection } from "./ProductImageSection";

export namespace MarketplaceProduct {
  // Everything is optional. Component with empty props is used as placeholder.
  export interface IProps {
    recordAdImpression?: boolean;
    children?: React.ReactNode | undefined;
    className?: string;
    role?: string;
    productTile?: ILeanProductTile;
    brand?: ILeanProductTile.IBrand;

    title?: string | undefined;

    // onClick is just for side-effect purposes, this component will navigate to
    // the url provided by generateProductUrl
    onClick?: (
      product: ILeanProductTile.ILeanProduct,
      event: React.MouseEvent<HTMLElement>
    ) => void;
    generateProductUrl?: (productToken: string) => string | undefined;

    onQuickAdd?: (newQuantity?: number) => void;
    onQuickAddSubmit?: (newQuantity?: number) => void;

    onChooseOption?: () => void;
    onHeartClicked?: () => void;
    onSaveToBoard?: (boardToken?: string) => void;
    onRemoveFromBoard?: (boardToken?: string) => void;
    onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;

    // onBrandClick is just for side-effect purposes, this component will
    // navigate to the url provided by generateBrandUrl
    onBrandClick?: (
      productToken: string,
      brandToken: string,
      isPromoted?: boolean,
      promotedBy?: Array<keyof typeof IProduct.PromotedType>,
      impressionId?: string
    ) => void;
    generateBrandUrl?: (brandToken: string) => string | undefined;
    hideCartQuantity?: boolean;
    showVerticalImagery?: boolean;
    onProductVisible?: (
      productToken?: string,
      topLeftTileBadge?: IBadge
    ) => void;
    shouldHideQuickAdd?: boolean;
    shouldShowPromotedBadge?: boolean;
    altTag?: string;
    useLazyImageLoading?: boolean;
    imageDimension?: IProductImageProps["imageDimension"];
    shouldHideSaveToBoard?: boolean;
    imageBreakpoints?: string;
  }
}

const MarketplaceProductLoggedIn: React.FC<MarketplaceProduct.IProps> = (
  props
) => {
  const {
    recordAdImpression,
    productTile,
    brand,
    onQuickAdd,
    onQuickAddSubmit,
    onChooseOption,
    onHeartClicked,
    onSaveToBoard,
    onRemoveFromBoard,
    onChooseOptionQuantityUpdate,
    generateBrandUrl,
    generateProductUrl,
    showVerticalImagery,
    onClick,
    onProductVisible,
    shouldShowPromotedBadge,
    altTag,
    useLazyImageLoading,
    imageDimension,
    shouldHideQuickAdd,
    shouldHideSaveToBoard,
    imageBreakpoints,
  } = props;

  const {
    recordProductBadgeViewEvent,
    recordBrandNameClickEvent,
    recordProductTileClickEvent,
    recordHeartClickedEvent,
    recordProductTileRightClickEvent,
    recordSaveToBoardSubmitEvent,
    recordRemoveFromBoardSubmitEvent,
    recordQuickAddButtonClickEvent,
    recordQuickAddSubmitEvent,
    recordChooseOptionClickEvent,
    recordChooseOptionQuantityUpdateSubmitEvent,
  } = useProductTileClickImpressions(
    productClickForSearchPageParametersILeanProductTileAdapter(productTile),
    brand,
    recordAdImpression
  );

  const viewport = useViewport();

  const topLeftTileBadge = useRef<IBadge>();

  const onTopLeftTileBadgeVisible = (tileBadge?: IBadge) => {
    recordProductBadgeViewEvent();
    topLeftTileBadge.current = tileBadge;
    onProductVisible?.(productTile?.product?.token, tileBadge);
  };

  const onProductClick = (
    productTile: ILeanProductTile,
    event: React.MouseEvent<HTMLElement>
  ) => {
    preloadNavigationToPDP({
      productToken: productTile.product?.token,
      bestImage: productTile.best_image,
      images: productTile.images,
      showVerticalImagery,
    });
    recordProductTileClickEvent();
    if (productTile.product) {
      onClick?.(productTile.product, event);
    }
  };

  const onBrandClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    recordBrandNameClickEvent();
    if (productTile?.product && props.onBrandClick) {
      props.onBrandClick(
        productTile.product.token!,
        productTile.product.brand_token!,
        productTile.product.is_promoted,
        productTile.product.promoted_by,
        productTile.product.impression_id
      );
    }

    event.stopPropagation();
  };

  const handleOnHeartClicked = () => {
    recordHeartClickedEvent();
    onHeartClicked?.();
  };
  const handleOnSaveToBoard = (boardToken?: string) => {
    recordSaveToBoardSubmitEvent(boardToken);
    onSaveToBoard?.(boardToken);
  };
  const handleOnRemoveFromBoard = (boardToken?: string) => {
    recordRemoveFromBoardSubmitEvent(boardToken);
    onRemoveFromBoard?.(boardToken);
  };

  const handleQuickAdd = (newQuantity?: number) => {
    recordQuickAddButtonClickEvent();
    onQuickAdd?.(newQuantity);
  };
  const handleQuickAddSubmit = (newQuantity?: number) => {
    recordQuickAddSubmitEvent(newQuantity);
    onQuickAddSubmit?.(newQuantity);
  };
  const handleChooseOption = () => {
    recordChooseOptionClickEvent();
    onChooseOption?.();
  };
  const handleChooseOptionQuantityUpdate = (newQuantity?: number) => {
    recordChooseOptionQuantityUpdateSubmitEvent(newQuantity);
    onChooseOptionQuantityUpdate?.(newQuantity);
  };

  const productUrl = productTile?.product?.token
    ? generateProductUrl?.(productTile.product.token)
    : undefined;

  return (
    <DiscoveryProductTile
      productTile={productTile}
      showVerticalImagery={props.showVerticalImagery}
      brand={brand}
      onBrandLinkClick={onBrandClick}
      generateBrandUrl={generateBrandUrl}
      onProductLinkClick={onProductClick}
      onTopLeftBadgeVisible={onTopLeftTileBadgeVisible}
      altText={altTag}
      productUrl={productUrl}
      shouldShowPromotedBadge={shouldShowPromotedBadge}
      onContextMenu={recordProductTileRightClickEvent}
      onHeartClicked={handleOnHeartClicked}
      onSaveToBoard={handleOnSaveToBoard}
      onRemoveFromBoard={handleOnRemoveFromBoard}
      onQuickAdd={handleQuickAdd}
      onQuickAddSubmit={handleQuickAddSubmit}
      onChooseOption={handleChooseOption}
      onChooseOptionQuantityUpdate={handleChooseOptionQuantityUpdate}
      hideAddToCartAndNotifyMe={viewport.isMobile || shouldHideQuickAdd}
      useLazyImageLoading={useLazyImageLoading}
      recordAdImpression={recordAdImpression}
      imageDimension={imageDimension}
      shouldHideSaveToBoard={shouldHideSaveToBoard}
      imageBreakpoints={imageBreakpoints}
    />
  );
};

type IMarketplaceProductLoggedOutProps = Pick<
  MarketplaceProduct.IProps,
  | "recordAdImpression"
  | "productTile"
  | "brand"
  | "children"
  | "generateBrandUrl"
  | "generateProductUrl"
  | "showVerticalImagery"
  | "onClick"
  | "onBrandClick"
  | "onProductVisible"
  | "altTag"
  | "useLazyImageLoading"
  | "imageBreakpoints"
>;

const MarketplaceProductLoggedOut = ({
  recordAdImpression,
  productTile,
  brand,
  children,
  generateBrandUrl,
  generateProductUrl,
  showVerticalImagery,
  onClick,
  onBrandClick,
  onProductVisible,
  altTag,
  useLazyImageLoading,
  imageBreakpoints,
}: IMarketplaceProductLoggedOutProps) => {
  const trackProductTileProps = useProductTileViewImpression(
    productTile?.product,
    !recordAdImpression,
    productTile?.settings_to_assign_on_view
  );
  const {
    recordProductBadgeViewEvent,
    recordBrandNameClickEvent,
    recordProductTileClickEvent,
    recordProductTileRightClickEvent,
  } = useProductTileClickImpressions(
    productClickForSearchPageParametersILeanProductTileAdapter(productTile),
    brand,
    recordAdImpression
  );
  const recordSignUpModalOpenClickEvent = useTrackSignupModalOpenClick();
  const topLeftTileBadge = useRef<IBadge>();

  const ID = `product-tile-${productTile?.product?.token}`;

  const isLoaded = !!productTile;

  const onTopLeftTileBadgeVisible = (tileBadge?: IBadge) => {
    recordProductBadgeViewEvent();
    topLeftTileBadge.current = tileBadge;
    onProductVisible?.(productTile?.product?.token, tileBadge);
  };

  const onProductClick = (
    productTile: ILeanProductTile,
    event: React.MouseEvent<HTMLElement>
  ) => {
    recordProductTileClickEvent();
    preloadNavigationToPDP({
      productToken: productTile.product?.token,
      bestImage: productTile.best_image,
      images: productTile.images,
      showVerticalImagery,
    });
    if (productTile.product) {
      onClick?.(productTile.product, event);
    }
    recordSignUpModalOpenClickEvent(
      SignUpType.PRODUCT,
      SignUpElement.PRODUCT_GRID,
      `logged_out_product_tile`
    );
  };

  const onBrandLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    recordBrandNameClickEvent();
    if (productTile?.product && onBrandClick) {
      onBrandClick(
        productTile.product.token!,
        productTile.product.brand_token!,
        productTile.product.is_promoted,
        productTile.product.promoted_by,
        productTile.product.impression_id
      );
    }

    event.stopPropagation();
  };

  const productUrl = productTile?.product?.token
    ? generateProductUrl?.(productTile.product.token)
    : undefined;

  if (getSettingEnableLoggedOutProductTileRedesign()) {
    return (
      <DiscoveryProductTile
        productTile={productTile}
        showVerticalImagery={showVerticalImagery}
        brand={brand}
        generateBrandUrl={generateBrandUrl}
        onProductLinkClick={onProductClick}
        onTopLeftBadgeVisible={onTopLeftTileBadgeVisible}
        altText={altTag}
        productUrl={productUrl}
        shouldShowPromotedBadge={false}
        onContextMenu={recordProductTileRightClickEvent}
        useLazyImageLoading={useLazyImageLoading}
        recordAdImpression={recordAdImpression}
        imageBreakpoints={imageBreakpoints}
      />
    );
  }

  return (
    // trackProductTileProps should ALWAYS wrap the outermost div of the product tile
    <Container
      {...trackProductTileProps}
      onContextMenu={recordProductTileRightClickEvent}
    >
      <ColumnWrapper
        id={`product-tile-${productTile?.product?.token}`}
        data-test-id={isLoaded ? "product-tile" : "product-tile-placeholder"}
      >
        <ProductImageSection
          altTag={altTag}
          brand={brand}
          productTile={productTile}
          onProductClick={onProductClick}
          generateProductUrl={generateProductUrl}
          showVerticalImagery={showVerticalImagery}
          onTopLeftBadgeVisible={onTopLeftTileBadgeVisible}
          useLazyImageLoading={useLazyImageLoading}
          imageBreakpoints={imageBreakpoints}
        />
        {children}
        <LoggedOutDetailSection
          brand={brand}
          productTile={productTile}
          productTileId={ID}
          onBrandLinkClick={onBrandLinkClick}
          onProductLinkClick={onProductClick}
          generateBrandUrl={generateBrandUrl}
          generateProductUrl={generateProductUrl}
        />
      </ColumnWrapper>
    </Container>
  );
};

const ColumnWrapper = styled(Column)`
  height: 100%;
`;

const MarketplaceProductBase: React.FC<MarketplaceProduct.IProps> = (props) => {
  if (isLoggedInRetailer()) {
    return (
      <MarketplaceProductLoggedIn
        {...props}
        imageBreakpoints={PRODUCT_GRID_IMAGE_BREAKPOINTS}
      />
    );
  }

  return (
    <MarketplaceProductLoggedOut
      {...props}
      imageBreakpoints={PRODUCT_GRID_IMAGE_BREAKPOINTS}
    />
  );
};

export const MarketplaceProduct = styled(MarketplaceProductBase)``;

export const FallbackContainer = createFallbackHandler(
  styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;

    > span:nth-child(2) {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
  `,
  {
    backgroundStyle: css`
      ${FallbackOpacityAnimation("0.2s")}
    `,
    loadedStyle: css`
      width: 100%;
      ${FallbackOpacityAnimation("0.2s")}
    `,
  }
);
